import React, { useState, useMemo, useRef } from "react";
import { Input } from "./ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { searchICD10, ICD10_CHAPTERS } from "../lib/icd10Codes";
import { Search, BookMarked } from "lucide-react";

/**
 * ICD-10 searchable picker.
 * Free-text is preserved — typing a custom code still works.
 * Selecting a suggestion fills the input with just the code (e.g. "E11.9")
 * so the printed Rx stays compact; the label is shown inside the dropdown only.
 * If parent also passes `onDiagnosisLabelPick`, we optionally forward the label
 * so the primary-diagnosis field can auto-fill in one click.
 */
export default function ICD10Combobox({ value, onChange, onDiagnosisLabelPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  const results = useMemo(() => searchICD10(q || value || "", 60), [q, value]);
  const grouped = useMemo(() => {
    const map = {};
    results.forEach((r) => {
      (map[r.chapter] = map[r.chapter] || []).push(r);
    });
    // Preserve chapter order defined in the dataset.
    return ICD10_CHAPTERS.filter((c) => map[c]).map((c) => [c, map[c]]);
  }, [results]);

  const pick = (row) => {
    onChange(row.code);
    if (onDiagnosisLabelPick) onDiagnosisLabelPick(row.label);
    setQ("");
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); setQ(e.target.value); }}
            onFocus={() => setOpen(true)}
            placeholder="Search ICD-10 code or type…"
            className="pr-8 tabular"
            data-testid="dx-icd10-combobox"
          />
          <BookMarked size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[440px] p-0 max-h-[420px] overflow-hidden flex flex-col"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b border-slate-200 shrink-0">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 230+ ICD-10 codes (name, code, alias)…"
              className="h-8 pl-7 text-sm"
              data-testid="dx-icd10-search"
              autoFocus
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {results.length === 0 && (
            <div className="p-4 text-center text-xs text-slate-500">
              No match. Free-text OK — type a custom code.
            </div>
          )}
          {grouped.map(([chapter, rows]) => (
            <div key={chapter} className="border-b border-slate-100 last:border-b-0">
              <div className="px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-semibold sticky top-0">
                {chapter}
              </div>
              {rows.map((r) => (
                <button
                  key={r.code}
                  onClick={() => pick(r)}
                  className="w-full px-3 py-1.5 text-left hover:bg-emerald-50 text-xs text-slate-800 flex gap-2 items-start"
                  data-testid={`dx-icd10-opt-${r.code}`}
                >
                  <span className="font-mono text-[10.5px] text-slate-900 min-w-[62px] shrink-0">{r.code}</span>
                  <span className="flex-1 text-slate-700 leading-snug">{r.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[9.5px] text-slate-500">
          Free-text preserved — clicking a code stores just the code. Curated Indian OP subset.
        </div>
      </PopoverContent>
    </Popover>
  );
}
